import React from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { Err, Ok, type Result } from '@lib/result';

import { ThemeProvider, ThemeWrapper } from '@/components/contexts/theme';

import ErrorFallbackWithoutServices from './components/molecules/error-fallback/without-services';
import { createCriticalServicesContainer } from './service-containers/critical/container';
import { CriticalServiceContainerProvider } from './service-containers/critical/context';
import { rootRouter } from './app.routes';

// note: we assert as non-null because we know the element exists in the HTML file
// if it doesn't, we have bigger problems...
const rootElement = document.getElementById('root')!;
const root = createRoot(rootElement);

const renderApp = async () => {
  let criticalServicesContainerResult: Result<
    Awaited<ReturnType<typeof createCriticalServicesContainer>>,
    Error
  >;

  try {
    criticalServicesContainerResult = Ok(await createCriticalServicesContainer());
  } catch (error) {
    const resolvedError =
      error instanceof Error
        ? error
        : new Error('Unknown error creating critical services container', { cause: error });
    criticalServicesContainerResult = Err(resolvedError);
  }

  if (criticalServicesContainerResult.ok === true) {
    root.render(
      <CriticalServiceContainerProvider container={criticalServicesContainerResult.value}>
        <ThemeProvider>
          <ThemeWrapper>
            <RouterProvider router={rootRouter} />
          </ThemeWrapper>
        </ThemeProvider>
      </CriticalServiceContainerProvider>
    );
  } else {
    // We can't render the default ErrorFallback here because it depends on SupportLink
    // which depends on Intercom, which is a service that may not be initialized
    // at this point in the app lifecycle.
    // So we render a simpler version of the error fallback that doesn't depend on services.
    root.render(
      <ThemeProvider>
        <ThemeWrapper>
          <ErrorFallbackWithoutServices error={criticalServicesContainerResult.error} />
        </ThemeWrapper>
      </ThemeProvider>
    );
  }
};

renderApp();
